import {
  type ComponentProps,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from "react"
import {
  DialogContent as DialogContentBase,
  DialogPortal,
  DialogOverlay,
} from "@/v2-ui/dialog/base/DialogBase"
import cn from "@/v2-ui/utils/utils.cn"
import { useQuerySelectorLazy } from "@/v2-ui/utils/utils.dom"

const DEFAULT_PORTAL_CONTAINER = "#mount"
/** Hit area of the browser's native resize grip, bottom-right. */
const RESIZE_GRIP_PX = 16
/** Gap kept between a resized dialog and the viewport edge. */
const RESIZE_EDGE_PX = 16
/** Where `dialog.css` turns the grip on; keep the two in step. */
const RESIZE_MEDIA_QUERY = "(min-width: 768px) and (pointer: fine)"

type DialogResizableSize = {
  width: string
  height: string
}

export type DialogContentProps = ComponentProps<typeof DialogContentBase> & {
  portalContainer?: string
  /** Vertical alignment of the dialog. Defaults to `"center"`. Use `"top"` for content that changes height. */
  align?: "center" | "top"
  /** Drop the default padding and row gap, for dialogs whose header/footer are full-bleed bands. */
  flush?: boolean
  /** Max width in px. Defaults to the base `max-w-lg`. */
  width?: number
  /** Render the full-screen backdrop. Set to `null` if you don't want a backdrop, or provide a custom React node. */
  renderOverlay?: ReactNode | (() => ReactNode) | null
  /**
   * Opening size, and the smallest the dialog gets (CSS lengths, e.g.
   * `"min(56rem,92vw)"`). With a desktop pointer it also adds the native
   * resize grip (bottom-right), which enlarges the dialog up to the
   * viewport. Leave width/height/max-width classes off: they fight it.
   */
  resizable?: DialogResizableSize
}

/**
 * The dialog is centered with `translate(-50%, -50%)`, so the native grip
 * would grow it in both directions and trail the pointer. When a press
 * lands on the grip, pin the box's top-left corner where it is: it then
 * grows right and down only, and stops at the viewport edge.
 * The grip hit-tests as the dialog itself, so a press on a child (a button
 * in the corner, a portaled popover) never pins.
 */
function pinAtResizeGrip(event: PointerEvent<HTMLDivElement>) {
  const element = event.currentTarget
  if (event.target !== element) return
  if (!window.matchMedia(RESIZE_MEDIA_QUERY).matches) return
  const rect = element.getBoundingClientRect()
  const isOnGrip =
    event.clientX > rect.right - RESIZE_GRIP_PX &&
    event.clientY > rect.bottom - RESIZE_GRIP_PX
  if (!isOnGrip) return

  Object.assign(element.style, {
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    transform: "none",
    // The exit animation slides by -50%, which no longer centers a pinned
    // box; with no animation Radix unmounts it at once on close.
    animation: "none",
    // Viewport units, so the cap follows a window resize.
    maxWidth: `calc(100vw - ${rect.left + RESIZE_EDGE_PX}px)`,
    maxHeight: `calc(100vh - ${rect.top + RESIZE_EDGE_PX}px)`,
  })
}

function DialogContent(props: DialogContentProps) {
  const {
    portalContainer = DEFAULT_PORTAL_CONTAINER,
    align = "center",
    flush = false,
    width,
    renderOverlay = <DialogOverlay />,
    resizable,
    className,
    style,
    onPointerDown,
    ...dialogContentBaseProps
  } = props
  const portalElem = useQuerySelectorLazy(portalContainer)

  return (
    <DialogPortal container={portalElem}>
      {renderOverlay && typeof renderOverlay === "function"
        ? renderOverlay()
        : renderOverlay}
      <DialogContentBase
        className={cn(
          "ui-dialog-content",
          align === "top" && "ui-dialog-content-align-top",
          flush && "ui-dialog-content-flush",
          resizable && "ui-dialog-content-resizable",
          className,
        )}
        style={
          resizable
            ? ({
                "--ui-dialog-width": resizable.width,
                "--ui-dialog-height": resizable.height,
                ...style,
              } as CSSProperties)
            : width
              ? { ...style, maxWidth: width }
              : style
        }
        onPointerDown={
          resizable
            ? (event) => {
                onPointerDown?.(event)
                pinAtResizeGrip(event)
              }
            : onPointerDown
        }
        {...dialogContentBaseProps}
      />
    </DialogPortal>
  )
}

export default DialogContent
